/**
 * HotkeySlotsPanel — modernized numpad-hotkey-slots section of the AbilityTestRig.
 *
 * Numpad 1-9 bindings persist via localStorage; the listener lives in
 * useGlobalInput (always-on, even with the rig closed).
 */
import React from 'react';
import { Section } from './shared';
import { rigStyles as styles } from './abilityRigStyles';
import type { AbilityMetadata } from '../../../systems/player/abilities/AbilityRegistry';
import { NUMPAD_SLOT_CODES, type HotkeyBindings } from '../../../systems/input/abilityTestHotkeys';
import { hasAbilityIcon, abilityIconPath } from '../../../rendering/abilities/AbilityIconRegistry';
import { ItemSlot } from '../../components/ItemKit';
import { Button } from '../../primitives';

export interface HotkeySlotsPanelProps {
  abilities: AbilityMetadata[];
  hotkeys: HotkeyBindings;
  onHotkeysChange: (next: HotkeyBindings) => void;
  onCast: (abilityId: string) => void;
}

export const HotkeySlotsPanel: React.FC<HotkeySlotsPanelProps> = ({
  abilities,
  hotkeys,
  onHotkeysChange,
  onCast,
}) => (
  <Section title="Numpad Hotkey Slots (1-9)">
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {NUMPAD_SLOT_CODES.map((code, i) => {
        const slotNum = i + 1;
        const bound = hotkeys[code] ?? '';
        const iconUrl = bound && hasAbilityIcon(bound) ? abilityIconPath(bound) : null;

        return (
          <div key={code} style={styles.hotkeyCard}>
            <span style={styles.keyBadge}>Num {slotNum}</span>

            {bound && (
              <ItemSlot
                item={{
                  id: bound,
                  name: bound,
                  icon: iconUrl || undefined,
                }}
                size="xs"
              />
            )}

            <select
              value={bound}
              onChange={(e) => {
                const next = { ...hotkeys };
                if (e.target.value) next[code] = e.target.value;
                else delete next[code];
                onHotkeysChange(next);
              }}
              style={styles.hotkeySelect}
            >
              <option value="">— Unbound —</option>
              {abilities.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name || a.id} ({a.id})
                </option>
              ))}
            </select>

            {bound && (
              <>
                <Button
                  variant="brass"
                  size="sm"
                  onClick={() => onCast(bound)}
                  title={`Fire slot ${slotNum} (${bound})`}
                >
                  ⚡ Fire
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    const next = { ...hotkeys };
                    delete next[code];
                    onHotkeysChange(next);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#ff6b6b',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    fontSize: 12,
                  }}
                  title="Unbind this slot"
                >
                  ✕
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  </Section>
);
